import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import FineTuningTable from "./FineTuningTable";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

// Define types for owner, approver, and application details for a FineTuningJob
interface FineTuningParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface FineTuningApplicationInfo {
    id: string;
    applicationName: string;
}

interface FineTuningSpecificDetails {
    baseModel?: string;
    tuningMethod?: string;
    trainingRegion?: string;
    acceleratorType?: string;
}

// Main FineTuningJob interface
interface FineTuningJob {
    id: string;
    name: string;
    application?: FineTuningApplicationInfo;
    cloudProvider?: string;
    solutionType?: string;
    costLabelPairs?: { cost: number; label: string }[];
    status: 'pending' | 'active' | 'rejected' | string;
    details?: {
        fineTuning?: FineTuningSpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: FineTuningParticipantInfo | null;
    solutionApprover?: FineTuningParticipantInfo | null;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof FineTuningJob | "solutionOwner.name" | "details.fineTuning.baseModel" | "details.fineTuning.tuningMethod" | "details.fineTuning.trainingRegion" | "application.applicationName" | string;
    groupBy: string;
}

const FineTuning: React.FC = () => {
    const [allFineTuningJobs, setAllFineTuningJobs] = useState<FineTuningJob[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);

    const initialColumnFilters: Record<string, boolean> = {
        name: true,
        application: true,
        cloudProvider: true,
        baseModel: true,
        trainingRegion: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetFineTunings {
                      getFineTunings {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        cloudProvider
                        solutionType
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          fineTuning {
                            baseModel
                            tuningMethod
                            trainingRegion
                            acceleratorType
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const fineTuningJobsData = response.getFineTunings;
                console.log("fine tuning data", fineTuningJobsData);
                if (fineTuningJobsData) {
                    const processedJobs = fineTuningJobsData.map((ft: any): FineTuningJob => ({
                        id: ft.id,
                        name: ft.name || "Unnamed Job",
                        application: ft.application ? {
                            id: ft.application.id,
                            applicationName: ft.application.applicationName,
                        } : undefined,
                        cloudProvider: ft.cloudProvider,
                        solutionType: ft.solutionType,
                        costLabelPairs: ft.costLabelPairs,
                        status: (ft.status || 'pending').toLowerCase(),
                        details: ft.details,
                        reasonForRejection: ft.reasonForRejection,
                        createdAt: ft.createdAt,
                        updatedAt: ft.updatedAt,
                        solutionOwner: ft.solutionOwner ? {
                            id: ft.solutionOwner.id,
                            name: ft.solutionOwner.name,
                            googleProfilePicture: ft.solutionOwner.googleProfilePicture,
                        } : null,
                        solutionApprover: ft.solutionApprover ? {
                            id: ft.solutionApprover.id,
                            name: ft.solutionApprover.name,
                            googleProfilePicture: ft.solutionOwner.googleProfilePicture,
                        } : null,
                    }));
                    setAllFineTuningJobs(processedJobs);

                    let dynamicSearchOptions: SearchOptions[] = processedJobs
                        .map((job: FineTuningJob) => [
                            { category: "name", groupBy: "JOB NAME", chipLabel: `Name: ${job.name}`, dropdownLabel: job.name },
                            ...(job.application ? [{ category: "application.applicationName" as any, groupBy: "APPLICATION", chipLabel: `App: ${job.application.applicationName}`, dropdownLabel: job.application.applicationName }] : []),
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${job.status}`, dropdownLabel: job.status.charAt(0).toUpperCase() + job.status.slice(1) },
                            ...(job.cloudProvider ? [{ category: "cloudProvider" as any, groupBy: "CLOUD PROVIDER", chipLabel: `Cloud: ${job.cloudProvider}`, dropdownLabel: job.cloudProvider }] : []),
                            ...(job.details?.fineTuning?.baseModel ? [{ category: "details.fineTuning.baseModel" as any, groupBy: "BASE MODEL", chipLabel: `Model: ${job.details.fineTuning.baseModel}`, dropdownLabel: job.details.fineTuning.baseModel }] : []),
                            ...(job.details?.fineTuning?.trainingRegion ? [{ category: "details.fineTuning.trainingRegion" as any, groupBy: "REGION", chipLabel: `Region: ${job.details.fineTuning.trainingRegion}`, dropdownLabel: job.details.fineTuning.trainingRegion }] : []),
                            ...(job.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${job.solutionOwner.name}`, dropdownLabel: job.solutionOwner.name }] : []),
                        ])
                        .flat();

                    dynamicSearchOptions = dynamicSearchOptions.filter(
                        (obj, index, self) =>
                            index === self.findIndex((t) =>
                                t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                            )
                    );
                    dynamicSearchOptions.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = String(a.dropdownLabel).toUpperCase();
                        const labelB = String(b.dropdownLabel).toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                    setSearchOptionsMasterList(dynamicSearchOptions);
                }
            } catch (error) {
                console.error("Error fetching Fine Tuning Jobs:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'name') return 'Job Name';
        if (key === 'application') return 'Application';
        if (key === 'cloudProvider') return 'Cloud Provider';
        if (key === 'baseModel') return 'Base Model';
        if (key === 'trainingRegion') return 'Training Region';
        if (key === 'solutionOwner') return 'Solution Owner';
        if (key === 'createdAt') return 'Added On';

        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredJobs =
        searchQuery.length === 0
            ? allFineTuningJobs
            : allFineTuningJobs.filter((job: FineTuningJob) => {
                return searchQuery.every(queryParam => {
                    let jobValue: any;
                    if (queryParam.category === "application.applicationName") {
                        jobValue = job.application?.applicationName;
                    } else if (queryParam.category === "solutionOwner.name") {
                        jobValue = job.solutionOwner?.name;
                    } else if (queryParam.category === "details.fineTuning.baseModel") {
                        jobValue = job.details?.fineTuning?.baseModel;

                    } else if (queryParam.category === "details.fineTuning.trainingRegion") {
                        jobValue = job.details?.fineTuning?.trainingRegion;
                    } else {
                        jobValue = job[queryParam.category as keyof FineTuningJob];
                    }

                    if (jobValue === null || jobValue === undefined) return false;
                    return String(jobValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
                });
            });

    const pendingJobs = globallyFilteredJobs.filter(job => job.status === 'pending' || job.status === 'submitted' || job.status === 'provisioning');
    const activeJobs = globallyFilteredJobs.filter(job => job.status === 'active' || job.status === 'approved' || job.status === 'success');
    const rejectedJobs = globallyFilteredJobs.filter(job => job.status === 'rejected' || job.status === 'failed');

    const totalJobsOverall = allFineTuningJobs.length;
    const totalMatchingSearch = globallyFilteredJobs.length;

    const canAddJob = user.roles.includes("cluster_owner") || user.roles.includes("viewer");


    const handleAddJob = () => {
        navigate("/aisolutions/finetuning/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"aisolutions-finetuning"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"aisolutions-finetuning"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{ xs: 1.5, sm: 2 }} mb="18px" >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx: 2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Fine Tuning Jobs
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box>
                                    {canAddJob && (
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "black", color: "white", padding: "8px 24px",
                                                fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                                borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
                                            }}
                                            onClick={handleAddJob}
                                            disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                        >
                                            Add New Fine Tuning Job
                                        </Button>
                                    )}
                                </Box>
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{
                                        padding: 0,
                                        border: 'none',
                                        ml: 2,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/Help.svg"
                                        alt="Help"
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            display: 'block',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by job name, model, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                                            sx={{
                                                                border: "1px solid #bbb",
                                                                borderRadius: "10px",
                                                                height: "45px",
                                                                width: "45px",
                                                            }}
                                                            onClick={handleColumnFilterClick}
                                                        >
                                                            <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                                                        </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{ xs: 1, sm: 2 }}>
                        {totalJobsOverall > 0 && totalMatchingSearch > 0 && (
                            <>
                                {/* Pending Accordion */}
                                <Accordion
                                    defaultExpanded={pendingJobs.length > 0}
                                    elevation={0}
                                    sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                                    disabled={pendingJobs.length === 0 && searchQuery.length > 0}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                            <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                Pending Jobs ({pendingJobs.length})
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                        {pendingJobs.length > 0 ? (
                                            <FineTuningTable
                                                jobs={pendingJobs}
                                                columnFilters={columnFilters}
                                                statusType="pending"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
                                                No pending jobs match your filters.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                                {/* Active Accordion */}
                                <Accordion
                                    defaultExpanded={activeJobs.length > 0}
                                    elevation={0}
                                    sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                                    disabled={activeJobs.length === 0 && searchQuery.length > 0}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                            <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                Active Jobs ({activeJobs.length})
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                        {activeJobs.length > 0 ? (
                                            <FineTuningTable
                                                jobs={activeJobs}
                                                columnFilters={columnFilters}
                                                statusType="active"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
                                                No active jobs match your filters.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                                {/* Rejected Accordion */}
                                <Accordion
                                    defaultExpanded={rejectedJobs.length > 0}
                                    elevation={0}
                                    sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                                    disabled={rejectedJobs.length === 0 && searchQuery.length > 0}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                            <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                Rejected Jobs ({rejectedJobs.length})
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                        {rejectedJobs.length > 0 ? (
                                            <FineTuningTable
                                                jobs={rejectedJobs}
                                                columnFilters={columnFilters}
                                                statusType="rejected"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
                                                No rejected jobs match your filters.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </>)}
                        {totalJobsOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Jobs Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Fine Tuning Jobs match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalJobsOverall === 0 && !isLoading && (
                            <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Jobs" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Fine Tuning Jobs are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first Fine Tuning Job.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default FineTuning;

// // Filename: RAG.tsx (or RAGPage.tsx as you have it)
// import React from "react"; // Removed unused useState
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     IconButton,
// } from "@mui/material";


// import { useAuth } from "../../../helpers/AuthProvider";
// import MainDrawer from "../../MainDrawer";
// import MiniBot from "../../MiniBot";

// // All dynamic data fetching, state management for RAGs, search, and column filters are removed
// // as RAGTable will now display static, hardcoded data and not accept data props.

// const FineTuning: React.FC = () => {
//     const { user } = useAuth();
//     const navigate = useNavigate();

//     const canAddFineTuning = true; // Example permission, adjust as needed

//     const handleAddFineTuning = () => {
//         // Navigate to an add page or handle as a placeholder for a static view
//         navigate("/aisolutions/finetuning/add"); // Adjust this path if necessary
//         // For a truly static page without add functionality, you might comment out the onClick or the button.
//     };

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"aisolutions-finetuning"} /> {/* Ensure 'rag' is a valid index for your MainDrawer */}
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                 mx:2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Fine Tuning
//                             </Typography>
//                             <Box sx={{display:'flex', justifyContent:'center'}}>
//                             {canAddFineTuning && (
//                                 <Button
//                                     variant="contained"
//                                     sx={{
//                                         backgroundColor: "black", color: "white", padding: "8px 24px",
//                                         fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                         borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                     }}
//                                     onClick={handleAddFineTuning}
//                                     // Adjust disabled logic based on RAG-specific permissions if different
//                                     disabled = {(user.roles.includes("cluster_owner") || user.roles.includes("viewer")) ? false : true}
//                                 >
//                                     Add New Fine Tuning Job
//                                 </Button>
//                             )}
//                             <IconButton
// 									  aria-label="Help" 
// 									  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
// 									  sx={{
// 										padding: 0, 
// 										border: 'none',  
// 										ml:2,
// 										'&:hover': {
// 										  backgroundColor: 'action.hover', 
// 										},
										
// 									  }}
// 									>
// 									  <Box
// 										component="img"
// 										src="/assets/Help.svg" 
// 										alt="" 
// 										sx={{
// 										  width: 40,  
// 										  height: 40,
// 										  display: 'block', 
// 										}}
// 									  />
// 									</IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         {/* Search and Filter UI have been removed as the table is now static */}
//                     </Box>

//                     <Box m={{xs: 1, sm:2}}>
//                         {/* RAGTable component will render its own static data */}
//                         <Box
//                                                             display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                                             sx={{
//                                                                 textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                                                 backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                                                 boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
//                                                                 height:"70vh"
//                                                             }}
//                                                         >
//                                                             <img src="/assets/inbox-icon.svg" alt="No AI Agents" style={{ width: '100px', marginBottom: '20px' }} />
//                                                             <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                                                 No Fine Tuning Job added yet
//                                                             </Typography>
//                                                             <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                                                 Click the button above to add your first Fine Tuning Job.
//                                                             </Typography>
//                                                         </Box>
                      
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default FineTuning;


// import { Box, Typography } from "@mui/material";
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import DashBoardPage from "../../DashBoardPage";
// import MainDrawer from "../../MainDrawer";
// import ComingSoonPage from "../../ComingSoonPage";
// const FineTuning: React.FC = () => {
//     return(<Box sx={{ display: "flex"}}>
//     <MainDrawer index={"aisolutions-finetuning"} />
// {/* Helllo */}
//     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100%", width:"100%"}}>
//  {/* <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography> */}
//  <ComingSoonPage/>
//     </Box>
//     </Box>)
// }
// export default FineTuning;